import { computed, watch } from 'vue'
import type { Ref } from 'vue'
import { useCubicleStore } from '@/store/cubicle.store'

/**
 * Box du bâtiment par défaut pour le bandeau PARC du tableau des compteurs (Mantis 0028608).
 *
 * Les box ne sont pas préchargés au démarrage (storeLoader) : seuls le plan de monte
 * (BuildingComposable), la cascade bâtiment/allée/box et les secteurs les chargent. Ouvert sans
 * être passé par l'un d'eux, le tableau des compteurs lisait un `cubicleStore.datas` vide et le
 * bandeau PARC restait vide.
 */

/** Chargement en cours, par centre : deux demandes rapprochées (montage + bâtiment connu un peu
 * plus tard) ne doivent pas lancer deux appels qui se remplaceraient dans `datas`. */
let pending: { centerId: number; promise: Promise<boolean> } | null = null

/**
 * Charge les box si `cubicleStore.datas` ne contient aucun box du bâtiment demandé.
 *
 * Même filtre que le plan de monte (BuildingComposable : tout le centre courant) et non le seul
 * bâtiment : `load` REMPLACE `datas`, et la cascade bâtiment/allée/box comme les secteurs ne
 * rechargent que « si vide » — un store réduit à un bâtiment leur cacherait les autres. Le plan de
 * monte recharge de toute façon à chaque ouverture, avec ce même filtre : aucun état écrasé.
 *
 * Rend true si les box du bâtiment sont disponibles (déjà là ou chargés), false sinon
 * (bâtiment/centre pas encore connus, échec API) — sans lever : un échec laisse seulement le
 * bandeau PARC vide, le reste du tableau ne dépend pas des box.
 */
export async function ensureCubiclesLoaded(buildingId: number, centerId: number | null | undefined): Promise<boolean> {
    if (!(buildingId > 0) || centerId == null) return false
    const cubicleStore = useCubicleStore()
    // Déjà chargés (plan de monte visité, ou chargement précédent) : pas d'appel à chaque ouverture.
    if (cubicleStore.datas.some((c) => c.buildingId === buildingId)) return true
    if (pending?.centerId === centerId) return pending.promise

    const promise = cubicleStore.load({ page: 1, pageSize: 0, centerId })
        .then((result) => !result?.error)
        .catch(() => false)
        .finally(() => {
            if (pending?.promise === promise) pending = null
        })
    pending = { centerId, promise }
    return promise
}

/**
 * Nombre de verrats placés par schéma dans le bâtiment donné, en chargeant les box s'il le faut.
 * Le bâtiment (option « bâtiment favori » ou premier bâtiment) et le centre peuvent n'être connus
 * qu'après le montage : on surveille donc les deux.
 */
export function useParcCubicles(buildingId: Ref<number>, centerId: Ref<number | null | undefined>) {
    const cubicleStore = useCubicleStore()

    watch([buildingId, centerId], async ([bldId, ctrId]) => {
        if (!(bldId > 0) || ctrId == null) return
        if (!(await ensureCubiclesLoaded(bldId, ctrId))) {
            console.warn(`[Compteurs] Box du bâtiment ${bldId} non chargés : bandeau PARC vide.`)
        }
    }, { immediate: true })

    const placedByGeneticType = computed<Map<number, number>>(() =>
        buildingId.value > 0 ? cubicleStore.getNbPlacedByGeneticType(buildingId.value) : new Map())

    return { placedByGeneticType }
}
